import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Box, Typography, TextField } from '@mui/material';
import { API_BASE_URL } from 'src/apiConfig';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from 'src/scenes/students/administration/helper/const';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

const ChooseSchoolForReview = () => {
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchId, setSearchId] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  useEffect(() => {
    setIsLoading(true);

    fetch(`${API_BASE_URL}/api/finalConnectingWork.php?action=getNotConnectWork`)
      .then((response) => response.json())
      .then((data) => setSchools(data))
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleReviewClick = (schoolId) => {
    const reviewerName = localStorage.getItem('username') || '';

    axios
      .post(`${API_BASE_URL}/api/finalSchools.php`, {
        action: 'startReviewingSchool',
        schoolId: schoolId,
        reviewerName: reviewerName
      })
      .then((response) => {
        if (response.data.success) {
          navigate(`/final-review-schools-students?classId=${schoolId}`);
        } else {
          const status = response.data.status;
          const existingReviewer = response.data.reviewer;

          let message = '';
          if (status === 'zavrseno') {
            message = t('review.finishedMessage', {
              reviewer: existingReviewer
            });
          } else if (status === 'pregledanje') {
            message = t('review.inProgressMessage', {
              reviewer: existingReviewer
            });
          } else {
            message = t('review.unknownStatusMessage');
          }

          setDialogMessage(message);
          setSelectedSchoolId(schoolId);
          setOpenDialog(true);
        }
      })
      .catch((error) => {
        console.error('There was an error starting the review!', error);
      });
  };

  const handleProceedAnyway = () => {
    setOpenDialog(false);
    navigate(`/final-review-schools-students?classId=${selectedSchoolId}`);
  };

  const columns = [
    { field: 'id', headerName: t('review.id'), width: 90 },
    { field: 'name', headerName: t('review.class'), width: 310 },
    {
      field: 'studentCountConnect',
      headerName: t('review.connected_works_count'),
      width: 180
    },
    {
      field: 'status',
      headerName: t('review.status'),
      width: 150
    },
    {
      field: 'profesor_name',
      headerName: t('review.professor_name'),
      width: 200
    },
    {
      field: 'action',
      headerName: t('review.action'),
      width: 180,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleReviewClick(params.row.id)}
          style={{
            backgroundColor: '#4cceac',
            color: '#fff',
            fontWeight: 'bold',
            textTransform: 'none',
            borderRadius: '8px'
          }}
        >
          {t('review.review')}
        </Button>
      )
    }
  ];

  const rows = schools
    .filter((school) => school.studentCountConnect > 0)
    .map((school) => {
      let statusInCyrillic = '';
      switch (school.status) {
        case 'zavrseno':
          statusInCyrillic = t('review.completed');
          break;
        case 'pregledanje':
          statusInCyrillic = t('review.in_progress');
          break;
        default:
          statusInCyrillic = t('review.not_started');
          break;
      }

      const profesorName =
        school.profesor_name === 'N/A' ? '-' : school.profesor_name;

      return {
        id: school.id,
        name: school.name,
        studentCount: school.studentCount,
        studentCountConnect: school.studentCountConnect,
        statusRaw: school.status,
        status: statusInCyrillic,
        profesor_name: profesorName
      };
    });

  const filteredRows = rows.filter((row) => {
    const matchStatus = statusFilter ? row.statusRaw === statusFilter : true;
    const matchId = searchId ? row.id.toString().includes(searchId) : true;
    return matchStatus && matchId;
  });

  return (
    <div
      style={{
        height: 600,
        width: '100%',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '16px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          marginBottom: '10px',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ fontWeight: 'bold' }}>
          {t('review.searchById')}
        </Typography>
        <TextField
          size="small"
          placeholder={t('review.searchById')}
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            input: { padding: '6px 10px' },
            width: '150px'
          }}
        />

        <Button
          variant={statusFilter === 'zavrseno' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('zavrseno')}
          sx={{
            backgroundColor:
              statusFilter === 'zavrseno' ? '#4cceac' : 'transparent',
            color: statusFilter === 'zavrseno' ? '#fff' : '#0d47a1',
            fontWeight: 'bold',
            border: `2px solid ${
              statusFilter === 'zavrseno' ? '#4cceac' : '#0d47a1'
            }`,
            textTransform: 'none'
          }}
        >
          {t('review.filterCompleted')}
        </Button>

        <Button
          variant={statusFilter === 'pregledanje' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('pregledanje')}
          sx={{
            backgroundColor:
              statusFilter === 'pregledanje' ? '#4cceac' : 'transparent',
            color: statusFilter === 'pregledanje' ? '#fff' : '#0d47a1',
            fontWeight: 'bold',
            border: `2px solid ${
              statusFilter === 'pregledanje' ? '#4cceac' : '#0d47a1'
            }`,
            textTransform: 'none'
          }}
        >
          {t('review.filterInProgress')}
        </Button>

        <Button
          variant={statusFilter === 'N/A' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('N/A')}
          sx={{
            backgroundColor: statusFilter === 'N/A' ? '#4cceac' : 'transparent',
            color: statusFilter === 'N/A' ? '#fff' : '#0d47a1',
            fontWeight: 'bold',
            border: `2px solid ${
              statusFilter === 'N/A' ? '#4cceac' : '#0d47a1'
            }`,
            textTransform: 'none'
          }}
        >
          {t('review.filterNotStarted')}
        </Button>

        <Button
          variant="outlined"
          onClick={() => setStatusFilter('')}
          sx={{
            color: '#0d47a1',
            fontWeight: 'bold',
            border: '2px solid #0d47a1',
            textTransform: 'none'
          }}
        >
          {t('review.resetFilter')}
        </Button>
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        checkboxSelection={false}
        disableColumnFilter
        loading={isLoading}
        components={{
          LoadingOverlay: LoadingOverlay
        }}
        disableColumnMenu
        componentsProps={{
          pagination: {
            labelRowsPerPage: t('pagination.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('pagination.of')} ${
                count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
              }`
          }
        }}
        sx={{
          boxShadow: 2,
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#3788d8',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 'bold',
            borderBottom: '2px solid #ddd'
          },
          '& .MuiDataGrid-cell': {
            color: '#0d47a1',
            fontSize: '14px'
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#e3f2fd',
            borderTop: '1px solid #ddd'
          }
        }}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{t('review.dialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            {t('review.cancel')}
          </Button>
          <Button onClick={handleProceedAnyway} color="secondary">
            {t('review.proceed')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ChooseSchoolForReview;
